/**
 * Rules Command
 * Manage persistent rules in .metis/rules/
 */

import kleur from 'kleur';
import fs from 'fs';
import path from 'path';
import { getRulesLoader } from '../../runtime/rulesLoader';

export async function runRules(args: string[]): Promise<void> {
  const rulesLoader = getRulesLoader();
  const subCommand = args[0] || 'list';

  switch (subCommand) {
    case 'list':
    case 'ls':
      listRules();
      break;

    case 'show':
      if (args[1]) {
        showRule(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /rules show <rule-name>'));
      }
      break;

    case 'create':
      if (args[1]) {
        await createRule(args[1], args.includes('--global'));
      } else {
        console.log(kleur.yellow('Usage: /rules create <name> [--global]'));
      }
      break;

    case 'delete':
      if (args[1]) {
        deleteRule(args[1], args.includes('--global'));
      } else {
        console.log(kleur.yellow('Usage: /rules delete <name> [--global]'));
      }
      break;

    case 'edit':
      if (args[1]) {
        editRule(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /rules edit <rule-name>'));
      }
      break;

    case 'refresh':
      refreshRules();
      break;

    case 'preview':
      previewCombinedRules();
      break;

    case 'help':
    default:
      showHelp();
  }
}

function listRules(): void {
  const rulesLoader = getRulesLoader();
  const rules = rulesLoader.getRulesList();
  const hierarchy = rulesLoader.loadRules();

  console.log(kleur.cyan('\n📜 Rules'));
  console.log(kleur.gray('─'.repeat(60)));

  if (rules.length === 0) {
    console.log(kleur.gray('No rules configured'));
    console.log();
    console.log(kleur.white('Create rules in:'));
    console.log(kleur.gray('  ~/.metis/rules/      - Global rules (all projects)'));
    console.log(kleur.gray('  .metis/rules/        - Project-specific rules'));
    return;
  }

  console.log(kleur.gray(`Total: ${rules.length} rule file(s)`));
  console.log();

  // Group by level
  const globalRules = rules.filter(r => r.level === 'global');
  const projectRules = rules.filter(r => r.level === 'project');

  if (globalRules.length > 0) {
    console.log(kleur.cyan('Global Rules (~/.metis/rules/):'));
    for (const rule of globalRules) {
      console.log(kleur.yellow(`  📄 ${rule.name}`));
    }
    console.log();
  }

  if (projectRules.length > 0) {
    console.log(kleur.cyan('Project Rules (.metis/rules/):'));
    for (const rule of projectRules) {
      console.log(kleur.yellow(`  📄 ${rule.name}`));
    }
    console.log();
  }

  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /rules show <name>   - View rule content'));
  console.log(kleur.gray('  /rules create <name> - Create new rule'));
  console.log(kleur.gray('  /rules preview       - Preview combined rules'));
}

function showRule(name: string): void {
  const rulesLoader = getRulesLoader();
  const rules = rulesLoader.getRulesList();

  // Find rule by name (with or without extension)
  const rule = rules.find(r =>
    r.name === name ||
    r.name === `${name}.md` ||
    r.name === `${name}.txt`
  );

  if (!rule) {
    console.log(kleur.red(`Rule not found: ${name}`));
    console.log(kleur.gray('Use /rules list to see available rules'));
    return;
  }

  try {
    const content = fs.readFileSync(rule.path, 'utf8');

    console.log(kleur.cyan(`\n📄 ${rule.name}`));
    console.log(kleur.gray(`Level: ${rule.level}`));
    console.log(kleur.gray(`Path: ${rule.path}`));
    console.log(kleur.gray('─'.repeat(60)));
    console.log(content);
    console.log(kleur.gray('─'.repeat(60)));
  } catch (error) {
    console.log(kleur.red(`Failed to read rule: ${error}`));
  }
}

async function createRule(name: string, global: boolean): Promise<void> {
  const rulesLoader = getRulesLoader();

  // Ensure name has extension
  const filename = name.endsWith('.md') ? name : `${name}.md`;

  // Create template content
  const template = `# ${formatRuleName(filename)}

<!-- Add your rules and guidelines here -->
<!-- These rules will be included in the AI's context -->

## Overview

Describe the purpose of these rules.

## Guidelines

1. First guideline
2. Second guideline
3. Third guideline

## Examples

\`\`\`
Add code examples if relevant
\`\`\`
`;

  try {
    const filePath = rulesLoader.createRule(filename, template, global);
    console.log(kleur.green(`✅ Rule created: ${filename}`));
    console.log(kleur.gray(`Path: ${filePath}`));
    console.log();
    console.log(kleur.cyan('Edit the rule file to add your guidelines'));
    console.log(kleur.gray('Use /rules refresh after editing to reload rules'));
  } catch (error) {
    console.log(kleur.red(`Failed to create rule: ${error}`));
  }
}

function deleteRule(name: string, global: boolean): void {
  const rulesLoader = getRulesLoader();

  const success = rulesLoader.deleteRule(name, global);

  if (success) {
    console.log(kleur.green(`✅ Rule deleted: ${name}`));
  } else {
    console.log(kleur.red(`Rule not found: ${name}`));
  }
}

function editRule(name: string): void {
  const rulesLoader = getRulesLoader();
  const rules = rulesLoader.getRulesList();

  const rule = rules.find(r =>
    r.name === name ||
    r.name === `${name}.md` ||
    r.name === `${name}.txt`
  );

  if (!rule) {
    console.log(kleur.red(`Rule not found: ${name}`));
    return;
  }

  console.log(kleur.cyan(`📝 Opening rule for editing: ${rule.name}`));
  console.log(kleur.gray(`Path: ${rule.path}`));
  console.log();
  console.log(kleur.yellow('Edit the file and run /rules refresh to reload'));

  // Try to open in editor
  const editor = process.env.EDITOR || process.env.VISUAL || 'code';
  console.log(kleur.gray(`\nRun: ${editor} "${rule.path}"`));
}

function refreshRules(): void {
  const rulesLoader = getRulesLoader();
  rulesLoader.clearCache();
  const hierarchy = rulesLoader.loadRules(true);

  console.log(kleur.green('✅ Rules refreshed'));
  console.log(kleur.gray(`Loaded ${hierarchy.totalFiles} rule file(s)`));
}

function previewCombinedRules(): void {
  const rulesLoader = getRulesLoader();
  const combined = rulesLoader.getCombinedRules();

  console.log(kleur.cyan('\n📜 Combined Rules Preview'));
  console.log(kleur.gray('─'.repeat(60)));

  if (!combined) {
    console.log(kleur.gray('No rules configured'));
    return;
  }

  // Show first 50 lines
  const lines = combined.split('\n');
  const preview = lines.slice(0, 50).join('\n');

  console.log(preview);

  if (lines.length > 50) {
    console.log();
    console.log(kleur.gray(`... and ${lines.length - 50} more lines`));
  }

  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.gray('These rules are included in the AI system prompt'));
}

function showHelp(): void {
  console.log(kleur.cyan('\n📜 Rules Command'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white('Manage persistent rules in .metis/rules/'));
  console.log();
  console.log(kleur.cyan('About Rules:'));
  console.log(kleur.gray('  Rules are markdown files with guidelines for the AI'));
  console.log(kleur.gray('  They are automatically loaded into the system context'));
  console.log(kleur.gray('  Similar to Claude Code\'s .claude/rules/ feature'));
  console.log();
  console.log(kleur.cyan('Rule Locations:'));
  console.log(kleur.gray('  ~/.metis/rules/    - Global rules (all projects)'));
  console.log(kleur.gray('  .metis/rules/      - Project-specific rules'));
  console.log();
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /rules             - List all rules'));
  console.log(kleur.gray('  /rules list        - List all rules'));
  console.log(kleur.gray('  /rules show <name> - View rule content'));
  console.log(kleur.gray('  /rules create <name> [--global] - Create new rule'));
  console.log(kleur.gray('  /rules delete <name> [--global] - Delete rule'));
  console.log(kleur.gray('  /rules edit <name> - Edit a rule'));
  console.log(kleur.gray('  /rules refresh     - Reload rules from disk'));
  console.log(kleur.gray('  /rules preview     - Preview combined rules'));
  console.log();
  console.log(kleur.cyan('Rule File Format:'));
  console.log(kleur.gray('  Use markdown (.md) or text (.txt) files'));
  console.log(kleur.gray('  Name files with priority prefix: 01-security.md'));
  console.log(kleur.gray('  Lower numbers = higher priority'));
}

function formatRuleName(filename: string): string {
  return filename
    .replace(/^\d+-/, '')
    .replace(/\.(md|txt)$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
